import { useState } from "react";
import FitResultDisplay from "./FitResultDisplay";

function CalculatorPage() {
  const [scenario, setScenario] = useState("door");
  const [form, setForm] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useDualHeight, setUseDualHeight] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const safeFloat = (val, fallback = 9999) => {
    const parsed = parseFloat(String(val || '').replace(",", "."));
    return isNaN(parsed) ? fallback : parsed;
  };
  
  const safeInt = (val, fallback = 0) => {
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? fallback : parsed;
  };

  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    setLoading(true);

    if (scenario === "stairs" && safeInt(form.turns) > 0 && !form.platform_depth) {
      setError("Platform Depth is required when there is at least one turn in the staircase.");
      setLoading(false);
      return;
    }

    // New logic: Determine product_height on the frontend based on the toggle.
    let productHeight;
    if (useDualHeight) {
      const rear = safeFloat(form.rear_height, 0);
      const front = safeFloat(form.front_height, 0);
      productHeight = Math.max(rear, front);
    } else {
      productHeight = safeFloat(form.product_height, 0);
    }

    // The payload now sends a single `product_height` and omits the dual height fields.
    const payload = {
      scenario,
      product_width: safeFloat(form.product_width, 0),
      product_depth: safeFloat(form.product_depth, 0),
      product_height: productHeight,

      door_width: safeFloat(form.door_width, 0),
      door_height: safeFloat(form.door_height, 0),
      side_wall_distance: safeFloat(form.side_wall_distance),
      front_wall_distance: safeFloat(form.front_wall_distance),

      stair_width: safeFloat(form.stair_width, 0),
      ceiling_height: safeFloat(form.ceiling_height, 0),
      turns: safeInt(form.turns),
      platform_depth: safeFloat(form.platform_depth, 0),
      stair_rise_height: safeFloat(form.stair_rise_height, 0),
      stair_tread_depth: safeFloat(form.stair_tread_depth, 0),
      stair_count: safeInt(form.stair_count),

      elevator_width: safeFloat(form.elevator_width, 0),
      elevator_depth: safeFloat(form.elevator_depth, 0),
      elevator_height: safeFloat(form.elevator_height, 0),
      elevator_door_width: safeFloat(form.elevator_door_width, 0),
      elevator_door_height: safeFloat(form.elevator_door_height, 0), // Added elevator door height
    };

    try {
      const res = await fetch("/api/calculate-fit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if(res.ok) {
        setResult(data);
      } else {
        setError(data.error || "An unknown error occurred.");
      }
    } catch (err) {
      setError("Failed to connect to the server. Please try again later.");
    } finally {
        setLoading(false);
    }
  };

  // --- UI Rendering Functions  (largely unchanged) ---

  const fieldStyle = { marginBottom: "1rem", display: "flex", flexDirection: "column" };
  const boxStyle = {
    border: "1px solid #ccc",
    padding: "1rem",
    borderRadius: "8px",
    marginBottom: "1.5rem",
    backgroundColor: "#f9f9f9",
  };

  const renderInput = (name, label, placeholder, tooltip, required = false) => (
    <div style={fieldStyle}>
      <label htmlFor={name} title={tooltip} style={{marginBottom: '0.5rem', fontWeight: 'bold'}}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </label>
      <input
        id={name}
        name={name}
        type="number"
        placeholder={placeholder}
        value={form[name] || ''}
        onChange={handleChange}
        style={{ padding: "10px", fontSize: "1rem", borderRadius: '4px', border: '1px solid #ccc' }}
      />
    </div>
  );

  const renderDoorFields = () => (
    <>
      {renderInput("door_width", "Door Width (in)", "e.g. 32", "Clear opening width of the doorway", true)}
      {renderInput("door_height", "Door Height (in)", "e.g. 80", "From floor to top of the door frame", true)}
      {renderInput("side_wall_distance", "Side Wall Distance (optional)", "e.g. 20", "Distance to wall beside door")}
      {renderInput("front_wall_distance", "Front Wall Distance (optional)", "e.g. 36", "Clearance space in front of door")}
    </>
  );

  const renderStairsFields = () => (
    <>
      {renderInput("stair_width", "Stair Width (in)", "e.g. 40", "Width between railings/walls", true)}
      {renderInput("ceiling_height", "Ceiling Height Over Stairs (in)", "e.g. 96", "Vertical space from stair tread to ceiling", true)}
      {renderInput("turns", "Number of Turns", "e.g. 1", "Count landings requiring a 90 or 180-degree turn", true)}
      {renderInput("platform_depth", "Platform/Landing Depth (in)", "e.g. 40", "Depth of the landing where you turn", safeInt(form.turns) > 0)}
      {renderInput("stair_rise_height", "Step Rise Height (in)", "e.g. 7", "Height of a single stair step")}
      {renderInput("stair_tread_depth", "Step Tread Depth (in)", "e.g. 11", "Depth of a single stair step (where you place your foot)")}
    </>
  );

  const renderElevatorFields = () => (
    <>
      {renderInput("elevator_width", "Elevator Interior Width (in)", "e.g. 60", "Side-to-side inside the elevator", true)}
      {renderInput("elevator_depth", "Elevator Interior Depth (in)", "e.g. 50", "Front-to-back inside the elevator", true)}
      {renderInput("elevator_height", "Elevator Interior Height (in)", "e.g. 84", "Floor-to-ceiling inside the elevator", true)}
      {renderInput("elevator_door_width", "Elevator Door Width (in)", "e.g. 36", "Clear opening width of the elevator door", true)}
      {renderInput("elevator_door_height", "Elevator Door Height (in)", "e.g. 80", "Clear opening height of the elevator door", true)}
    </>
  );
  
  const renderDualHeightToggle = () => (
    <div style={{ ...fieldStyle, flexDirection: "row", alignItems: "center", marginTop: '1rem' }}>
      <input
        type="checkbox"
        id="dualHeight"
        checked={useDualHeight}
        onChange={() => setUseDualHeight(!useDualHeight)}
        style={{ marginRight: "0.5rem", width: '20px', height: '20px' }}
      />
      <label htmlFor="dualHeight">Is the furniture height uneven (e.g., a sofa with a high back)?</label>
    </div>
  );

  const renderHeightFields = () => (
    <>
      {useDualHeight
        ? (
          <>
            {renderInput("rear_height", "Highest Point (in)", "e.g. 32", "The absolute highest vertical point of the item", true)}
            {renderInput("front_height", "Lowest Point (in)", "e.g. 28", "The height of the lower section (e.g., armrest)", true)}
          </>
        )
        : renderInput("product_height", "Furniture Height (in)", "e.g. 34", "Overall top-to-bottom vertical height", true)
      }
      {renderDualHeightToggle()}
    </>
  );

  return (
    <div style={{ padding: "2rem", maxWidth: "600px", margin: "auto", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      <h1 style={{textAlign: 'center'}}>Furniture Fit Calculator</h1>

      <p style={{ marginBottom: "1.5rem", color: "#555", textAlign: 'center' }}>
        Will your new furniture fit? Select a scenario, enter the dimensions, and find out instantly.
      </p>

      <fieldset style={boxStyle}>
        <legend><strong>1. Select Scenario</strong></legend>
        <div style={fieldStyle}>
          <select
            id="scenario"
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            style={{ padding: "10px", fontSize: "1rem", borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="door">Doorway</option>
            <option value="stairs">Stairway</option>
            <option value="elevator">Elevator</option>
          </select>
        </div>
      </fieldset>

      <fieldset style={boxStyle}>
        <legend><strong>2. Furniture Dimensions</strong></legend>
        {renderInput("product_width", "Furniture Width (in)", "e.g. 78", "Side-to-side when viewed from the front", true)}
        {renderInput("product_depth", "Furniture Depth (in)", "e.g. 36", "Front-to-back dimension", true)}
        {renderHeightFields()}
      </fieldset>

      <fieldset style={boxStyle}>
        <legend><strong>3. Access Path Dimensions</strong></legend>
        {scenario === "door" && renderDoorFields()}
        {scenario === "stairs" && renderStairsFields()}
        {scenario === "elevator" && renderElevatorFields()}
      </fieldset>

      {error && <p style={{ color: "red", fontWeight: "bold", textAlign: 'center' }}>Error: {error}</p>}

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          padding: "12px 20px",
          fontSize: "1.1rem",
          width: '100%',
          background: loading ? '#ccc' : "#ff7500",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: loading ? 'not-allowed' : 'pointer',
          fontWeight: 'bold'
        }}
      >
        {loading ? 'Calculating...' : 'Check Fit'}
      </button>

      {result && <FitResultDisplay response={result} />}
    </div>
  );
}

export default CalculatorPage;
